import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { getChannelDisplayName } from '@/utils/channelLabel';
import { initialsOf } from '@/components/PostComposer/sections/preview/previewIdentity';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';
import { CancelButton } from '@/components/ui/CancelButton';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PostEditor } from '@/components/composer/PostEditor';
import { MediaSection } from '@/components/PostComposer/sections/MediaSection';
import { LabelsSection } from '@/components/PostComposer/sections/LabelsSection';
import { PlatformsSection } from '@/components/PostComposer/sections/PlatformsSection';
import { ScheduleSection } from '@/components/PostComposer/sections/ScheduleSection';
import { AISection } from '@/components/PostComposer/sections/AISection';
import { HashtagSection } from '@/components/composer/HashtagSection';
import { CustomSelect, type SelectOption } from '@/components/ui/CustomSelect';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';
import { CreatorToolbar, type CreatorToggle } from '@/components/PostCreator/CreatorToolbar';
import { CreatorSectionCard } from '@/components/PostCreator/CreatorSectionCard';
import { useResizableColumns } from '@/components/PostCreator/useResizableColumns';
import { checkLimits } from '@/constants/platformLimits';
import { checkCaptionWithinLimits, getYoutubeEffectiveCaptionText } from '@/utils/captionLimit';
import { PostSelector } from './PostSelector';
import { useVariationComposer } from '@/hooks/useVariationComposer';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useCalendars } from '@/hooks/useCalendar';
import { useCalendarEvents } from '@/hooks/useCalendarEvents';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { usePostPermissions, variationToReleaseFacts, type ReleaseItemFacts } from '@/hooks/usePostPermissions';
import { usePostReleasePermissions } from '@/hooks/usePostReleasePermissions';
import { useReleaseCapabilities } from '@/hooks/useReleaseCapabilities';
import { ReleaseLockBanner, useReleaseLockText } from '@/components/release/ReleaseLockBanner';
import type { ReleaseDenyReason } from '@/types/release';
import { useChannels } from '@/hooks/useChannels';
import { Post, PostMedia } from '@/services/posts';
import { usePost } from '@/hooks/usePosts';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useVariationSettings } from '@/hooks/useVariationSettings';
import { SUPPORTED_LANGUAGES, getLanguageName } from '@/utils/languages';
import { variationsService } from '@/services/variations.service';
import { calendarMappingsService } from '@/services/calendar-mappings.service';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { getChannelAvatar } from '@/utils/channel-avatar';
import type { MediaType } from '@/constants/platformMediaRequirements';
import type { MediaItem } from '@/types/posts';
import type { PostVariation } from '@/types/variations';
import type { PlatformOptions } from '@/types/scheduled-instances';
import api, { formatApiError } from '@/services/api';

/**
 * Helper: Get default platform-specific options for new channels
 */
function getDefaultPlatformOptions(platform: string): PlatformOptions | null {
  const normalizedPlatform = platform.toLowerCase();

  switch (normalizedPlatform) {
    case 'instagram':
      return {
        instagram: {
          contentType: 'FEED', // Default to feed, user must opt-in for Reels
          shareReelsFeed: true,
        },
      };
    case 'facebook':
      return {
        facebook: {
          contentType: 'FEED',
        },
      };
    case 'youtube':
      return {
        youtube: {
          privacyStatus: 'public',
          isShort: false,
        },
      };
    case 'tiktok':
      return {
        tiktok: {
          privacyLevel: 'PUBLIC_TO_EVERYONE',
          disableComment: false,
          disableDuet: false,
          disableStitch: false,
        },
      };
    case 'linkedin':
      return {
        linkedin: {
          visibility: 'PUBLIC',
        },
      };
    case 'twitter':
    case 'x':
      return {
        twitter: {
          replySettings: 'everyone',
        },
      };
    case 'threads':
      return {
        threads: {
          replyControl: 'everyone',
        },
      };
    default:
      return null;
  }
}

/**
 * VariationCreator - Modal Component for Creating/Editing Variations
 *
 * Rewritten to match PostCreator's UI and logic:
 * - CreatorToolbar with expandable sections
 * - Save-first-then-shoot CTA
 * - Media inherit toggle
 * - Full schedule support with calendar mapping
 * - TypeScript strict typing
 *
 * Two-step workflow:
 * - Step 1: Post Selection (create mode only)
 * - Step 2: Variation Form (PostCreator-like layout)
 *
 * Features:
 * - Variation title (required identifier)
 * - Caption override (optional, inherits from original if empty)
 * - Media: inherit from original OR custom media
 * - Labels, hashtags, AI assistant
 * - Shoot now / schedule with calendar mapping
 * - Platform-specific options
 * - Preview section
 * - Dirty state tracking with confirmation
 * - Keyboard shortcuts
 */

export interface VariationCreatorProps {
  /**
   * Control modal open state
   */
  isOpen: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Optional: Existing variation for edit mode
   */
  variation?: PostVariation;

  /**
   * Optional: Pre-select a post (create mode)
   */
  preSelectedPostId?: number;

  /**
   * Optional: Calendar mapping ID for shooting mode (committing a mapped variation)
   */
  shootingMappingId?: number;

  /**
   * Optional: In shooting mode, open with the schedule/shoot panel expanded
   * (commit intent). Defaults to true (dashboard commit flow unchanged).
   * Calendar edit passes false so a mapped, uncommitted variation opens as an
   * editable view; the rocket toggle commits explicitly.
   */
  autoOpenScheduleOnShoot?: boolean;

  /**
   * Optional: open with the schedule panel expanded (global composer host, "schedule" entry mode).
   * The panel is still gated by the same permission verdict as the rocket toggle.
   */
  initialScheduleOpen?: boolean;

  /**
   * Optional: Callback after successful save
   */
  onSuccess?: () => void;
}

export const VariationCreator = ({
  isOpen,
  onClose,
  variation,
  preSelectedPostId,
  shootingMappingId,
  autoOpenScheduleOnShoot = true,
  initialScheduleOpen = false,
  onSuccess,
}: VariationCreatorProps) => {
  const { t, i18n } = useTranslation('common');
  const isShootingMode = !!shootingMappingId;
  const isEditMode = !!variation || isShootingMode;

  // Step state (1 = select post, 2 = form)
  // Start at step 2 if editing, pre-selected, or shooting mode (avoid flashing PostSelector while post loads)
  const [step, setStep] = useState<1 | 2>(isEditMode || preSelectedPostId || isShootingMode ? 2 : 1);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isRunningSaveAction, setIsRunningSaveAction] = useState(false);

  // Id of the variation whose data has been loaded into the composer (edit mode). Keyed by id, not a boolean:
  // this component stays mounted while closed, so `isEditMode` can flip to true AFTER first render (deep link
  // opens straight after page load); a boolean seeded from the first render would claim "loaded" and mount the
  // caption editor empty before the data arrives.
  const [loadedVariationId, setLoadedVariationId] = useState<number | null>(null);

  // Fetch mapping for shooting mode
  const { data: shootingMapping } = useQuery({
    queryKey: ['calendar-mapping', shootingMappingId],
    queryFn: () => calendarMappingsService.getMapping(shootingMappingId!),
    enabled: !!shootingMappingId && isOpen,
    staleTime: 0, // always fresh on open: scheduled time is seeded from plannedDate
  });

  // Fetch variation for shooting mode
  const { data: shootingVariation } = useQuery({
    queryKey: ['variation', shootingMapping?.postId, shootingMapping?.variationId],
    queryFn: () => variationsService.getById(shootingMapping!.postId, shootingMapping!.variationId!),
    enabled: !!shootingMapping?.variationId && isOpen,
    staleTime: 0, // always fresh on open; singular key is invalidated on variation save
  });

  // Fetch parent post for edit mode, pre-selected, or shooting mode
  const postIdToFetch = variation?.postId || preSelectedPostId || shootingMapping?.postId;
  const { data: fetchedPost } = usePost(postIdToFetch ?? 0, { enabled: !!postIdToFetch && isOpen });

  // Fetch title suggestions for autocomplete
  // Note: teamId is determined server-side from session, no need in query key
  const { data: titleSuggestions = [], isLoading: isTitlesLoading, isFetching } = useQuery({
    queryKey: ['variation-titles'],
    queryFn: () => variationsService.getTitles(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: isOpen && !isEditMode,
  });

  // Convert suggestions to Combobox options
  const titleOptions: ComboboxOption[] = titleSuggestions.map((s) => ({
    value: s.title,
    label: s.title,
    count: s.count,
  }));

  // Language choices: the brand's configured variation languages first, then the other supported ones.
  // A stored language outside both (e.g. imported) stays selectable so editing never silently drops it.
  const { teamId: languageTeamId } = useEffectiveTeamId();
  const { data: variationSettings } = useVariationSettings(languageTeamId ?? 0);

  // The effective brand changed while this editor is open: its content belongs to the previous brand, so close it
  // (the same rule as the post composer; the form state is dropped when the editor closes).
  const previousTeamIdRef = useRef(languageTeamId);
  useEffect(() => {
    const previous = previousTeamIdRef.current;
    previousTeamIdRef.current = languageTeamId;
    if (previous === null || previous === languageTeamId || !isOpen) return;
    toast.info(t('composer_closed_brand_changed'));
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageTeamId]);

  // Use shooting variation if in shooting mode, otherwise use passed variation
  const effectiveVariation = shootingVariation || variation;

  // Variation composer hook (local state, not Zustand)
  const composer = useVariationComposer({
    variation: effectiveVariation,
    parentPost: selectedPost,
    shootingMappingId,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  // Register this editor's dirty state globally while it is open, so the
  // auto-reload mechanism defers to a manual refresh banner instead of
  // silently discarding in-progress work. Component stays mounted regardless
  // of isOpen (parent toggles the isOpen prop), so gate on it.
  useUnsavedGuard(isOpen && composer.isDirty, 'variation-creator');

  const LANGUAGE_NONE = 'none';
  const languageOptions = useMemo<SelectOption[]>(() => {
    const codes: string[] = [];
    const add = (raw: string | null | undefined): void => {
      const code = (raw ?? '').trim().toLowerCase();
      if (code && !codes.includes(code)) codes.push(code);
    };
    (variationSettings?.variationLanguages ?? []).forEach(add);
    SUPPORTED_LANGUAGES.forEach(add);
    add(composer.language);
    return [
      { value: LANGUAGE_NONE, label: t('variation_language_none', { defaultValue: 'No language (custom version)' }) },
      ...codes.map((code) => ({ value: code, label: `${getLanguageName(code)} (${code.toUpperCase()})` })),
    ];
  }, [variationSettings?.variationLanguages, composer.language, t]);

  // Channels for platform mapping
  const { data: channels } = useChannels();

  // Client restrictions
  const { canSchedulePost, getRestrictionMessage } = useReviewerRestrictions();
  const permissions = usePostPermissions();
  const lockText = useReleaseLockText();

  // Server verdicts of the release rulebook for THIS variation (its own release record, its own
  // occurrences, the viewer's role). The parent post's state is never used for a variation.
  const releasePerms = usePostReleasePermissions(
    effectiveVariation?.postId ?? null,
    isOpen && !!effectiveVariation
  );
  const variationServerPerms = effectiveVariation
    ? releasePerms.forVariation(effectiveVariation.id)
    : undefined;

  // Create mode (a new version has no release record yet): the brand-level verdict of the CURRENT
  // effective team. Fail closed. An existing version asks its own verdict above.
  const createCaps = useReleaseCapabilities(isOpen && !isEditMode && !effectiveVariation);

  // Calendars
  const { data: calendars, isLoading: isLoadingCalendars } = useCalendars({ enabled: isOpen });

  // UI state
  const [activeToggles, setActiveToggles] = useState<Set<CreatorToggle>>(new Set());

  // Two-column layout: right-panel toggles drive desktop column split
  const RIGHT_PANEL_TOGGLES: CreatorToggle[] = ['media', 'ai', 'schedule', 'carousel'];
  // Raw toggle state (hook inputs that must run before the permission verdicts exist);
  // rendering uses the derived `hasRightPanelToggles` below, which honors scheduleVisible.
  const rawRightPanelToggles = RIGHT_PANEL_TOGGLES.some((toggle) => activeToggles.has(toggle));
  const { containerRef: resizeContainerRef, handleProps: resizeHandleProps, leftPercent: resizeLeftPercent } = useResizableColumns(rawRightPanelToggles);

  const [lightboxMedia, setLightboxMedia] = useState<PostMedia | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<'shoot_now' | 'schedule'>('shoot_now');
  const [selectedCalendar, setSelectedCalendar] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [showCtaDropdown, setShowCtaDropdown] = useState(false);

  // Calendar events
  const selectedDate = composer.scheduledTime || new Date();
  const { data: calendarEvents, isLoading: isLoadingEvents } = useCalendarEvents(
    selectedCalendar ? parseInt(selectedCalendar, 10) : null,
    selectedDate
  );

  // Track if user manually selected a post (to prevent the init effect from overwriting)
  const userSelectedPostRef = useRef(false);

  // Set selected post when fetched
  useEffect(() => {
    if (fetchedPost && isOpen) {
      setSelectedPost(fetchedPost);
    }
  }, [fetchedPost, isOpen]);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Don't override if user just manually selected a post
      if (userSelectedPostRef.current) return;

      if (isEditMode && effectiveVariation && selectedPost) {
        composer.loadVariation(effectiveVariation, selectedPost);
        setLoadedVariationId(effectiveVariation.id);
        setStep(2);
      } else if (isEditMode || preSelectedPostId || isShootingMode) {
        // Set step 2 immediately while post loads (avoids PostSelector flash)
        setStep(2);
      } else if (!selectedPost) {
        setStep(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, effectiveVariation?.id, selectedPost?.id, preSelectedPostId, isShootingMode]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setStep(1);
        setSelectedPost(null);
        setActiveToggles(new Set());
        setScheduleMode('shoot_now');
        setSelectedCalendar('');
        setSelectedEvent('');
        setShowCtaDropdown(false);
        setLoadedVariationId(null);
        hasAutoOpenedRef.current = false;
        userSelectedPostRef.current = false;
        composer.reset();
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Content is locked by the release rulebook for THIS variation: server verdict first (role, own release
  // record, scheduled/published = frozen_committed); the client mirror covers loading / failure.
  // Creating a new variation is a new entity (the create door is gated separately).
  const variationFacts: ReleaseItemFacts | null =
    isEditMode && effectiveVariation ? variationToReleaseFacts(effectiveVariation) : null;
  const canEditVariation: boolean = variationServerPerms
    ? variationServerPerms.canEdit
    : variationFacts
      ? permissions.canEditItem(variationFacts)
      : true;
  const isContentLocked = isEditMode && !!effectiveVariation && !canEditVariation;
  const lockReason: ReleaseDenyReason | null = isContentLocked
    ? variationServerPerms?.reasons.edit ??
      (variationFacts ? permissions.editDenyReasonItem(variationFacts) : null) ??
      'not_creator'
    : null;
  const lockMessage: string | null = isContentLocked ? lockText(lockReason) : null;
  // Scheduling verdict of THIS variation (a brand-new one is an unreleased draft)
  const canScheduleVariation: boolean = variationServerPerms
    ? variationServerPerms.canSchedule
    : permissions.canScheduleItem(
        variationFacts ?? { workflowStatus: 'UNSCHEDULED', hasPublishedOutput: false, isVariation: true, createdAt: new Date().toISOString() }
      );

  // ONE verdict for every schedule / publish-now / date-time control of this composer.
  // Workflow on: only this variation's own verdict (server SSOT; a new variation is an unreleased draft).
  // Workflow off: unchanged (role flag or the variation's verdict).
  const canScheduleHere: boolean = permissions.workflowEnabled ? canScheduleVariation : canSchedulePost || canScheduleVariation;

  // Auto-expand schedule section when editing a variation that has calendar mappings OR in shooting mode.
  // Locked variations keep the auto-open even when the caller opts out (mirrors PostCreator).
  useEffect(() => {
    const shootingAutoOpen = isShootingMode && (autoOpenScheduleOnShoot || (isContentLocked && canScheduleVariation));
    if (isOpen && ((isEditMode && !isShootingMode && effectiveVariation?.calendarMappings && effectiveVariation.calendarMappings.length > 0) || shootingAutoOpen)) {
      setActiveToggles(prev => new Set([...prev, 'schedule']));
    }
  }, [isEditMode, isShootingMode, autoOpenScheduleOnShoot, isContentLocked, canScheduleVariation, isOpen, effectiveVariation?.calendarMappings]);

  // Open the schedule panel when the caller asked for schedule mode (global composer host)
  useEffect(() => {
    if (isOpen && initialScheduleOpen) {
      setActiveToggles(prev => new Set([...prev, 'schedule']));
    }
  }, [isOpen, initialScheduleOpen]);

  // Initialize calendar from shooting mapping
  useEffect(() => {
    if (isShootingMode && shootingMapping?.calendarId && isOpen) {
      setSelectedCalendar(String(shootingMapping.calendarId));
      composer.setCalendarId(shootingMapping.calendarId);
    }
  }, [isShootingMode, shootingMapping?.calendarId, isOpen, composer]);

  // Initialize scheduled time from shooting mapping
  useEffect(() => {
    if (isShootingMode && shootingMapping?.plannedDate && !composer.scheduledTime && isOpen) {
      composer.setScheduledTime(new Date(shootingMapping.plannedDate));
    }
  }, [isShootingMode, shootingMapping?.plannedDate, composer.scheduledTime, isOpen, composer]);

  // Initialize platform options with sensible defaults when channels are selected
  useEffect(() => {
    if (!channels || !isOpen) return;

    const selectedIds = composer.selectedChannels;
    const currentOptions = composer.channelOptions;

    // Find channels that need default options initialized
    for (const channelId of selectedIds) {
      // Skip if options already exist for this channel
      if (currentOptions[channelId]) continue;

      const channel = channels.find((c: { id: number }) => c.id === channelId);
      if (!channel) continue;

      const defaults = getDefaultPlatformOptions(channel.platform);
      if (defaults) {
        // Initialize this channel's options with platform defaults
        composer.updateChannelOptions(channelId, defaults, true);
      }
    }
  }, [channels, composer.selectedChannels, composer.channelOptions, isOpen]);

  // Auto-toggle carousel mode in edit mode based on media count
  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      if (composer.media.length > 1) {
        // Multiple media → auto-enable carousel
        composer.setCarouselMode(true);
      } else {
        // 0 or 1 media → auto-disable carousel (no longer a carousel)
        composer.setCarouselMode(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, composer.media.length]);

  // Sync calendar to composer
  const handleCalendarChange = (value: string) => {
    setSelectedCalendar(value);
    composer.setCalendarId(value ? parseInt(value, 10) : null);
  };

  // Parse selectedEvent string to extract calendarNoteId or calendarHolidayId
  const parseSelectedEvent = (event: string): { calendarNoteId?: number; calendarHolidayId?: number } => {
    if (!event) return {};
    const [type, idStr] = event.split('-');
    const id = parseInt(idStr, 10);
    if (isNaN(id)) return {};
    if (type === 'note') return { calendarNoteId: id };
    if (type === 'holiday') return { calendarHolidayId: id };
    return {};
  };

  // Sync selectedEvent to composer
  useEffect(() => {
    const parsed = parseSelectedEvent(selectedEvent);
    composer.setCalendarEvent(parsed.calendarNoteId ?? null, parsed.calendarHolidayId ?? null);
  }, [selectedEvent, composer]);

  // Transform calendars to SelectOption format
  const calendarOptions: SelectOption[] = useMemo(() => {
    if (!calendars) return [];
    return calendars.map((cal) => ({
      value: String(cal.id),
      label: cal.name,
      icon: ICONS.CALENDAR,
      iconColor: cal.color || undefined,
    }));
  }, [calendars]);

  // Transform events to SelectOption format - filtered by selected date
  const eventOptions: SelectOption[] = useMemo(() => {
    if (!calendarEvents) return [];

    // Filter events that match the selected date (respecting repeat patterns)
    const filteredEvents = calendarEvents.filter((event) => {
      const eventStart = new Date(event.startDate);
      const eventEnd = event.endDate ? new Date(event.endDate) : null;
      const targetDate = new Date(selectedDate);

      // Normalize to date only (ignore time)
      eventStart.setHours(0, 0, 0, 0);
      if (eventEnd) eventEnd.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      // Target must be on or after event start
      if (targetDate < eventStart) return false;

      // If there's an end date, target must be on or before it
      if (eventEnd && targetDate > eventEnd) return false;

      // Holidays don't repeat - they're single-day events
      if (event.type === 'holiday') {
        return targetDate.getTime() === eventStart.getTime();
      }

      // Notes: handle repeat types
      const repeatType = event.repeatType;

      // Non-repeating note: check if within range
      if (!repeatType || repeatType === 'NONE') {
        // Single-day (no endAt) - must be same day
        if (!eventEnd) {
          return targetDate.getTime() === eventStart.getTime();
        }
        // Multi-day (has endAt) - already validated within range
        return true;
      }

      // Handle repeat types (same logic as campaignMatcher.ts)
      switch (repeatType) {
        case 'DAILY':
          return true; // Every day within range

        case 'WEEKLY':
          // Same day of week as startDate
          return targetDate.getDay() === eventStart.getDay();

        case 'MONTHLY':
          // Same day of month as startDate
          return targetDate.getDate() === eventStart.getDate();

        case 'YEARLY':
          // Same month and day as startDate
          return (
            targetDate.getMonth() === eventStart.getMonth() &&
            targetDate.getDate() === eventStart.getDate()
          );

        default:
          return false;
      }
    });

    return filteredEvents.map((event) => {
      let description = '';
      let icon: string = ICONS.CALENDAR;

      // For notes - check repeatType to determine if Content Pillar or Campaign
      if (event.type === 'note') {
        const isRepeating = event.repeatType && event.repeatType !== 'NONE';
        description = isRepeating
          ? t('event_type_content_pillar', { defaultValue: 'Content Pillar' })
          : t('event_type_campaign', { defaultValue: 'Campaign' });
        // Content Pillar = REFRESH (recurring), Campaign = FLAG
        icon = isRepeating ? ICONS.REFRESH : ICONS.FLAG;
      }

      // For holidays - show country code if available
      if (event.type === 'holiday') {
        description = event.countryCode
          ? t('event_type_holiday_country', { defaultValue: 'Holiday · {{country}}', country: event.countryCode })
          : t('event_type_holiday', { defaultValue: 'Holiday' });
        icon = ICONS.GIFT;
      }

      return {
        value: `${event.type}-${event.id}`,
        label: event.title,
        icon,
        description,
      };
    });
  }, [calendarEvents, selectedDate, t]);

  // Auto-open ref for labels/hashtags
  const hasAutoOpenedRef = useRef(false);

  // Scroll fade indicator state
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [checkScroll, rawRightPanelToggles]);

  // Re-check scroll after toggle animations complete
  useEffect(() => {
    const timer = setTimeout(checkScroll, 350);
    return () => clearTimeout(timer);
  }, [activeToggles, checkScroll]);

  /**
   * Get selected platforms from selected channel IDs
   */
  const selectedPlatforms = (): string[] => {
    if (composer.selectedChannels.length === 0) {
      return ['linkedin'];
    }

    const activeChannels = channels?.filter((ch) =>
      composer.selectedChannels.includes(ch.id)
    );

    if (!activeChannels || activeChannels.length === 0) {
      return ['linkedin'];
    }

    return [...new Set(activeChannels.map((ch) => ch.platform.toLowerCase()))];
  };

  /**
   * Get channel preview data for PreviewSection
   */
  const channelPreviewData = useMemo(() => {
    if (!channels || composer.selectedChannels.length === 0) return [];
    return channels
      .filter((ch) => composer.selectedChannels.includes(ch.id))
      .map((ch) => ({
        channelId: ch.id,
        platform: ch.platform.toLowerCase(),
        displayName: getChannelDisplayName(ch),
        initials: initialsOf(getChannelDisplayName(ch)),
        handle: ch.platformUsername || '',
        avatarUrl: getChannelAvatar(ch.metadata as Record<string, unknown> | null | undefined) ?? undefined,
        options: composer.channelOptions[ch.id] || {},
      }));
  }, [channels, composer.selectedChannels, composer.channelOptions]);

  /**
   * Check if any media is video type (for platform options validation)
   */
  const hasVideo = useMemo(() => {
    return composer.media.some((m) => m.type === 'video');
  }, [composer.media]);

  /**
   * Effective media for preview — inherit mode uses parent post media, custom uses composer media
   */
  const previewMedia: MediaItem[] = useMemo(() => {
    if (composer.mediaMode === 'custom') return composer.media;
    if (!selectedPost?.postMedia?.length) return [];
    return selectedPost.postMedia.map((m) => {
      // For videos, never fall back to originalUrl (the MP4) as a thumbnail —
      // that would make the browser render an MP4 inside an <img> element.
      // Leave thumbnail undefined so MediaSection shows the placeholder icon.
      const thumbnail: string | undefined =
        m.type === 'VIDEO'
          ? (m.thumbnailUrl ?? undefined)
          : (m.thumbnailUrl || m.originalUrl || undefined);
      return {
        id: m.id.toString(),
        url: m.originalUrl ?? '',
        thumbnail,
        type: m.type.toLowerCase() as 'image' | 'video' | 'pdf',
        size: m.size || 0,
      };
    });
  }, [composer.mediaMode, composer.media, selectedPost]);

  /**
   * Extract media types for platform compatibility
   */
  const currentMediaTypes = (): MediaType[] => {
    if (composer.media.length === 0) {
      return [];
    }
    const types = composer.media.map((item) => item.type.toUpperCase() as MediaType);
    return [...new Set(types)];
  };

  /**
   * Auto-open Labels, Hashtags, and Media toggles when variation has existing values
   */
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;

    const hasLabels = composer.labels.length > 0;
    const hasHashtags = /#\w+/.test(composer.caption);
    const hasMedia = composer.mediaMode === 'custom' && composer.media.length > 0;

    if (hasLabels || hasHashtags || hasMedia) {
      hasAutoOpenedRef.current = true;
      setActiveToggles((prev) => {
        const newSet = new Set(prev);
        if (hasLabels) newSet.add('labels');
        if (hasHashtags) newSet.add('hashtags');
        if (hasMedia) newSet.add('media');
        return newSet;
      });
    }
  }, [composer.labels, composer.caption, composer.media.length, composer.mediaMode]);

  /**
   * Handle image insertion from AI Briefing Modal (Unsplash)
   * Tracks download with Unsplash API and uploads image to our server
   */
  const handleInsertImage = useCallback(async (imageUrl: string, credit: string, downloadUrl: string) => {
    try {
      // Track download with Unsplash (API compliance)
      if (downloadUrl) {
        api.post('/api/unsplash/download', { downloadUrl }).catch((err: unknown) => {
          console.warn('Failed to track Unsplash download:', err);
        });
      }

      // Upload image from URL to our server (longer timeout for large images)
      const response = await api.post('/api/media/upload-url', { url: imageUrl }, { timeout: 60000 });
      const uploadedMedia = response.data.data;

      // Create MediaItem from the uploaded media
      const mediaItem: MediaItem = {
        id: uploadedMedia.id.toString(),
        url: uploadedMedia.originalUrl,
        thumbnail: uploadedMedia.thumbnailUrl || uploadedMedia.originalUrl,
        type: 'image',
        filename: uploadedMedia.filename || credit,
        size: uploadedMedia.fileSize || 0,
      };

      // Auto-switch from inherit to custom when adding new media
      if (composer.mediaMode === 'inherit') {
        composer.setMediaMode('custom');
      }

      // Add to media
      composer.addMedia(mediaItem);

      // Append Unsplash photo credit to caption (deduped)
      if (credit) {
        const photographerName = credit.replace(/^(Photo by |ai_briefing_image_photo_by )/i, '');
        const creditLine = `📷 Credit: ${photographerName} on Unsplash`;
        const currentCaption = composer.caption;

        if (!currentCaption.includes(creditLine)) {
          const hasExistingCredits = currentCaption
            .split('\n')
            .some((line) => line.startsWith('📷 Credit:'));

          const updatedCaption = hasExistingCredits
            ? `${currentCaption}\n${creditLine}`
            : `${currentCaption}${currentCaption.trim() ? '\n\n' : ''}${creditLine}`;

          composer.setCaption(updatedCaption);
        }
      }

      // Open media section so user can see the image
      if (!activeToggles.has('media')) {
        setActiveToggles((prev) => new Set([...prev, 'media']));
      }

      toast.success(t('image_added', { defaultValue: 'Image added to variation' }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      const errorMessage = formatApiError(error).message;
      toast.error(errorMessage);
    }
  }, [composer, activeToggles, t]);

  /**
   * Handle toolbar toggle
   */
  const handleToggle = (id: CreatorToggle) => {
    const isOpening = !activeToggles.has(id);

    setActiveToggles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });

    // Sync carousel toggle with composer
    if (id === 'carousel') {
      composer.setCarouselMode(isOpening);
    }

    // Scroll to the opened section after render
    if (isOpening) {
      requestAnimationFrame(() => {
        const el =
          document.getElementById(`variation-section-${id}-mobile`) ||
          document.getElementById(`variation-section-${id}`) ||
          document.getElementById(`variation-section-${id}-left`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /**
   * Handle close with dirty state check
   */
  const handleClose = () => {
    if (composer.isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  /**
   * Confirm discard
   */
  const handleConfirmDiscard = () => {
    composer.handleDiscard();
    setShowDiscardConfirm(false);
    onClose();
  };

  /**
   * Get CTA label based on current state
   */
  const isActivelyShooting = isShootingMode && activeToggles.has('schedule');
  // Derived, never enforced by mutating the toggle: visible only while scheduling is allowed (shooting keeps its flow)
  const scheduleVisible: boolean = activeToggles.has('schedule') && (!permissions.workflowEnabled || canScheduleHere || isShootingMode);
  // Right column exists only when a toggle that renders there is actually visible (a denied schedule toggle must not leave an empty column + divider)
  const hasRightPanelToggles: boolean = RIGHT_PANEL_TOGGLES.some((toggle) => (toggle === 'schedule' ? scheduleVisible : activeToggles.has(toggle)));
  // Now/Later pill: workflow off keeps the old role flag so it behaves exactly as before
  const showNowLaterPill: boolean = permissions.workflowEnabled ? canScheduleHere : canSchedulePost;
  const rocketOn = isActivelyShooting || scheduleVisible;

  // Save-button dropdown, release workflow ON (carte blanche OFF) and rocket/schedule mode OFF.
  // An existing version uses its own verdict (server SSOT); a new version uses the brand capabilities.
  // While loading, on error, or on an older server: nothing is shown.
  const releaseMenuVerdictReady: boolean =
    permissions.workflowEnabled &&
    !(rocketOn && canScheduleHere) &&
    !isContentLocked &&
    (effectiveVariation ? !releasePerms.isLoading : !createCaps.isLoading);
  const showReleaseDirect: boolean =
    releaseMenuVerdictReady &&
    (effectiveVariation ? variationServerPerms?.canReleaseDirect === true : createCaps.canReleaseDirect);
  const showSaveAndSendForReview: boolean =
    releaseMenuVerdictReady &&
    (effectiveVariation ? variationServerPerms?.canSendForReview === true : createCaps.canSendForReview);
  const hasReleaseMenu: boolean = showReleaseDirect || showSaveAndSendForReview;
  const menuOnly: boolean = rocketOn && hasReleaseMenu;

  const getCtaLabel = useCallback((): { label: string; materialIcon: string; showDropdown: boolean } => {
    if (rocketOn) {
      const isShootNow = scheduleMode === 'shoot_now';
      return {
        label: isShootNow ? t('shoot_now', { defaultValue: 'Shoot Now' }) : t('schedule_post', { defaultValue: 'Schedule' }),
        materialIcon: isShootNow ? 'rocket_launch' : 'schedule',
        // Schedule panel open but this version can't be scheduled yet: only the review / release items are offered
        showDropdown: hasReleaseMenu,
      };
    }

    // Check if variation is READY
    const isReady = effectiveVariation?.contentStatus === 'READY';
    if (isReady) {
      return { label: t('save_variation', { defaultValue: 'Save Variation' }), materialIcon: 'check_circle', showDropdown: hasReleaseMenu };
    }

    // Default: save with dropdown for "Save & Mark Ready"
    // Hide dropdown when workflow is enabled (Mark Ready is replaced by approval flow)
    return { label: t('save_draft', { defaultValue: 'Save Draft' }), materialIcon: 'check_circle', showDropdown: !permissions.workflowEnabled || hasReleaseMenu };
  }, [rocketOn, scheduleMode, effectiveVariation?.contentStatus, permissions.workflowEnabled, hasReleaseMenu, t]);

  /**
   * Get disabled reason for CTA
   */
  const getDisabledReason = useCallback((): string | null => {
    // Frozen content: nothing may be saved (only the rocket path can still be open, re-sending stored content)
    if (isContentLocked && !rocketOn) {
      return lockMessage;
    }

    if (!composer.canSaveDraft) {
      return t('title_required', { defaultValue: 'Title is required' });
    }

    if (!rocketOn) return null;

    const saveHint = isShootingMode
      ? ''
      : ` · ${t('toggle_rocket_to_save', { defaultValue: 'Toggle the rocket off to just save' })}`;

    if (composer.selectedChannels.length === 0) {
      return t('select_channel', { defaultValue: 'Select at least one channel' }) + saveHint;
    }

    if (!isShootingMode && !composer.calendarId) {
      return t('select_calendar', { defaultValue: 'Select a calendar' }) + saveHint;
    }

    // Check approval workflow restrictions using centralized permissions
    if (!canScheduleVariation) {
      const denied = variationServerPerms?.reasons.schedule;
      const message = denied
        ? i18n.t(`errors:release.${denied}`, { keySeparator: false })
        : getRestrictionMessage('schedule post');
      return message + saveHint;
    }

    if (scheduleMode === 'schedule' && !composer.scheduledTime) {
      return t('pick_datetime', { defaultValue: 'Pick a date and time' }) + saveHint;
    }

    if (scheduleMode === 'schedule' && composer.scheduledTime && composer.scheduledTime <= new Date()) {
      return t('future_time', { defaultValue: 'Must be in the future' }) + saveHint;
    }

    // Character limit check. YouTube validates its per-channel description override
    // (if set), not the caption — same source of truth as the submit-handler
    // re-validation in useVariationComposer.handlePrimaryAction (checkCaptionWithinLimits).
    if (composer.caption.trim()) {
      const platforms = selectedPlatforms();
      const youtubeText = getYoutubeEffectiveCaptionText(
        composer.caption,
        composer.selectedChannels,
        composer.channelOptions,
        channels
      );
      const captionCheck = checkCaptionWithinLimits(composer.caption, platforms, { youtube: youtubeText });
      if (!captionCheck.isWithinAll) {
        return t('caption_over_limit', { defaultValue: 'Caption too long for: {{platforms}}', platforms: captionCheck.overPlatformNames }) + saveHint;
      }
    }

    // Image count check
    const imageCount = composer.media.filter((m) => m.type.toLowerCase() === 'image').length;
    if (imageCount > 0) {
      const platforms = selectedPlatforms();
      const imgCheck = checkLimits(imageCount, platforms, 'carouselImages');
      if (!imgCheck.isWithinAll) {
        const overInfo = imgCheck.overLimits
          .map((o: { displayName: string; limit: number }) => `${o.displayName} (max ${o.limit})`)
          .join(', ');
        return t('images_over_limit', { defaultValue: 'Too many images for: {{platforms}}', platforms: overInfo }) + saveHint;
      }
    }

    return null;
  }, [composer, rocketOn, scheduleMode, isContentLocked, lockMessage, canScheduleVariation, variationServerPerms, i18n, getRestrictionMessage, t]);

  /**
   * Handle primary action
   */
  const handlePrimaryAction = () => {
    // Auto-switch to inherit if custom media is empty (better UX than blocking); never on frozen content
    if (!isContentLocked && composer.mediaMode === 'custom' && composer.media.length === 0) {
      composer.setMediaMode('inherit');
    }

    if (rocketOn) {
      composer.handlePrimaryAction({
        rocketOn: true,
        shootMode: scheduleMode,
        channelIds: composer.selectedChannels,
        scheduledTime: composer.scheduledTime,
        channelOptions: composer.channelOptions,
      });
    } else {
      composer.handleSave();
    }
  };

  const isBusy = composer.isBusy || isRunningSaveAction;

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    if (!isOpen || step !== 2) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Enter to execute primary action
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        const disabledReason = getDisabledReason();
        if (!disabledReason && !isBusy && !releasePerms.isLoading) {
          handlePrimaryAction();
        }
      }

      // Cmd/Ctrl + S to save draft
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (!isShootingMode && composer.canSaveDraft && !isBusy && !isContentLocked && !releasePerms.isLoading) {
          composer.handleSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, activeToggles, scheduleMode, composer]);

  /** Run a save-then-act handler with the footer marked busy for the whole save + follow-up call. */
  const runSaveAction = async (handler: () => Promise<void>): Promise<void> => {
    setIsRunningSaveAction(true);
    try {
      await handler();
    } finally {
      setIsRunningSaveAction(false);
    }
  };

  const ctaConfig = getCtaLabel();
  const disabledReason = getDisabledReason();
  // Keep Save disabled until the server verdict arrives (no editable flash on a frozen variation)
  const isDisabled = !!disabledReason || isBusy || releasePerms.isLoading;

  // Hidden toggles
  const hiddenToggles = useMemo(() => {
    const hidden = new Set<CreatorToggle>();

    // Hide schedule toggle using centralized permissions
    if (!canScheduleHere && !isShootingMode) {
      hidden.add('schedule');
    }

    // Hide carousel toggle in edit mode or shooting mode (auto-carousel with multiple media)
    if (isEditMode || isShootingMode) {
      hidden.add('carousel');
    }

    return hidden;
  }, [canScheduleHere, isShootingMode, isEditMode]);

  return (
    <>
      <TooltipProvider>
        <Modal open={isOpen} onOpenChange={handleClose} size="2xl" raw={true} preventClose={isBusy} className="!rounded-md">
          {/* Header */}
          <div className="relative flex-shrink-0 flex items-center justify-between px-6 sm:px-[33px] pt-3 pb-3">
            <h2 className="text-lg sm:text-xl md:text-2xl font-wagner text-gray-900 w-full text-center lg:text-left pr-10">
              {isShootingMode && rocketOn
                ? t('shoot_variation', { defaultValue: 'Shoot Variation' }) + (effectiveVariation ? `: ${effectiveVariation.title}` : '')
                : isEditMode
                ? t('edit_variation', { defaultValue: 'Edit Variation' }) + (effectiveVariation ? `: ${effectiveVariation.title}` : '')
                : t('create_variation', { defaultValue: 'Create Variation' })}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={isBusy}
              aria-label={t('close', { defaultValue: 'Close' })}
              className="absolute top-3 right-4 sm:right-6 max-sm:top-[3px] max-sm:right-[6px] max-sm:p-[15px] p-2 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name={ICONS.CROSS} size="sm" color="text-gray-500" />
            </button>
          </div>

          {step === 1 ? (
            /* Step 1: Post Selection */
            <div className="flex-1 overflow-y-auto px-6 sm:px-[33px] pb-8">
              <div className="mt-4">
                <PostSelector
                  onSelect={(post) => {
                    userSelectedPostRef.current = true;
                    setSelectedPost(post);
                    setStep(2);
                  }}
                  disabled={isBusy}
                />
              </div>
            </div>
          ) : !selectedPost ? (
            /* Step 2 loading: waiting for post data */
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3 text-gray-400">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-400 rounded-full animate-spin" />
                <span className="text-sm">{t('loading_post', { defaultValue: 'Loading post...' })}</span>
              </div>
            </div>
          ) : (
            <>
              {/* ── Two-column body (lg+) / single-column (mobile) ── */}
              {/*
               * lg+: flex-row — left col (55%) has title + toolbar + caption,
               *      right col (45%, only when right-panel toggles active) has media/ai/schedule/preview.
               * <lg: flex-col — all sections scroll in single column.
               */}
              <div
                ref={resizeContainerRef}
                className={cn(
                  'flex-1 min-h-0 flex',
                  // Short viewports (keyboard open): the body scrolls so the footer stays on screen
                  'flex-col max-lg:overflow-y-auto',
                  hasRightPanelToggles && 'lg:flex-row'
                )}
              >

                {/* ── LEFT COLUMN: Title + Toolbar + Caption ── */}
                <div
                  style={hasRightPanelToggles ? { width: `${resizeLeftPercent}%` } : undefined}
                  className={cn(
                    cn('px-6 sm:px-[33px] pt-4 max-lg:!w-auto', hasRightPanelToggles && 'lg:pr-4'),
                    hasRightPanelToggles || activeToggles.size > 0
                      ? 'flex-1 min-h-0 overflow-y-auto lg:overflow-visible lg:flex lg:flex-col lg:flex-none lg:min-w-0 lg:pb-4 scrollbar-none'
                      : 'flex-shrink-0 lg:w-full pb-4'
                  )}
                >
                  {/* Error Display */}
                  {composer.isError && (
                    <div className="flex items-start gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                      <Icon name={ICONS.CAUTION} size="sm" color="text-red-600" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-600">{t('error', { defaultValue: 'Error' })}</p>
                        <p className="text-sm text-red-600">
                          {formatApiError(composer.error).message}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Original Post Reference */}
                  {selectedPost && (
                    <div className="flex-shrink-0 mb-3 flex items-center gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                      {/* Thumbnail */}
                      <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                        {selectedPost.postMedia && selectedPost.postMedia.length > 0 && selectedPost.postMedia[0].thumbnailUrl ? (
                          <img
                            src={selectedPost.postMedia[0].thumbnailUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Icon name={ICONS.FILE} size="xs" color="text-gray-400" ariaHidden />
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex-shrink-0">
                          {t('original', { defaultValue: 'Original' })}
                        </span>
                        <span className="text-xs text-gray-700 truncate flex-1">
                          {selectedPost.caption || t('no_caption', { defaultValue: 'No caption' })}
                        </span>
                        {!isEditMode && !isShootingMode && (
                          <button
                            type="button"
                            onClick={() => setStep(1)}
                            disabled={isBusy}
                            className="flex-shrink-0 text-[10px] font-medium text-primary-500 hover:text-primary-600 underline underline-offset-2 disabled:opacity-50"
                          >
                            {t('change', { defaultValue: 'Change' })}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Read-only reason: frozen content stays visible, never blank */}
                  {isContentLocked && (
                    <ReleaseLockBanner reason={lockReason} className="flex-shrink-0 mb-3" />
                  )}

                  {/* Title Input with Autocomplete (variation-specific, required) */}
                  <div className="flex-shrink-0 mb-3">
                    <Combobox
                      id="variation-title"
                      value={composer.title}
                      onChange={composer.setTitle}
                      options={titleOptions}
                      placeholder={t('variation_title_placeholder', { defaultValue: 'Variation title (e.g., EN, ES, Version B)...' })}
                      required
                      disabled={isBusy || isContentLocked}
                      loading={isTitlesLoading || isFetching}
                      emptyMessage={t('no_matching_titles', { defaultValue: 'No matching titles — type to create new' })}
                      className="border-0 border-b-2 border-gray-200 focus-within:border-primary-400 rounded-none bg-transparent text-lg font-medium"
                    />
                  </div>

                  {/* Explicit language of this version (independent of the title) */}
                  <div className="flex-shrink-0 mb-3">
                    <CustomSelect
                      id="variation-language"
                      label={t('variation_language_label', { defaultValue: 'Language' })}
                      options={languageOptions}
                      value={composer.language ?? LANGUAGE_NONE}
                      onChange={(value) => composer.setLanguage(value === LANGUAGE_NONE ? null : value)}
                      disabled={isBusy || isContentLocked}
                      helperText={t('variation_language_hint', {
                        defaultValue: 'Used to label and filter this version. Renaming the title does not change it.',
                      })}
                      compact
                    />
                  </div>

                  {/* Icon Toolbar — above caption */}
                  <div className="flex-shrink-0 mb-3">
                    <CreatorToolbar
                      activeToggles={activeToggles}
                      onToggle={handleToggle}
                      disabled={isBusy}
                      showCarousel={composer.media.length > 1}
                      hiddenToggles={hiddenToggles}
                    />
                  </div>

                  {/* Caption Editor - delay mount in edit mode until variation data is loaded */}
                  {(!isEditMode || (effectiveVariation !== undefined && loadedVariationId === effectiveVariation.id)) ? (
                    <PostEditor
                      value={composer.caption}
                      onChange={composer.setCaption}
                      platforms={selectedPlatforms()}
                      disabled={isBusy || isContentLocked}
                      compact={!hasRightPanelToggles}
                      fillHeight={hasRightPanelToggles}
                      className={cn(
                        hasRightPanelToggles
                          ? 'lg:flex-shrink lg:flex-1 lg:min-h-0 lg:[&_[contenteditable]]:!h-full'
                          : ''
                      )}
                      placeholder={
                        selectedPost.caption
                          ? t('variation_caption_placeholder', { defaultValue: 'Override caption or leave empty to inherit...' })
                          : t('whats_on_your_mind', { defaultValue: "What's on your mind?" })
                      }
                      selectedChannels={composer.selectedChannels}
                      onInsertImage={handleInsertImage}
                    />
                  ) : (
                    <div className="h-[180px] border border-gray-200 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-400 rounded-full animate-spin" />
                    </div>
                  )}

                  {/* Hashtags & Labels — below caption on desktop only */}
                  {(activeToggles.has('hashtags') || activeToggles.has('labels')) && (
                    <div className="hidden lg:block mt-3 space-y-3">
                      {activeToggles.has('hashtags') && (
                        <CreatorSectionCard id="variation-section-hashtags-left" icon="tag" title={t('hashtags', { defaultValue: 'Hashtags' })}>
                          <HashtagSection caption={composer.caption} onCaptionChange={composer.setCaption} readOnly={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                      {activeToggles.has('labels') && (
                        <CreatorSectionCard id="variation-section-labels-left" icon="label" title={t('organize', { defaultValue: 'Organize' })}>
                          <LabelsSection labels={composer.labels} onChange={composer.setLabels} disabled={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                    </div>
                  )}

                  {/* Mobile: all toggled sections in correct order (scrolls with left column) */}
                  {activeToggles.size > 0 && (
                    <div className="lg:hidden mt-4 space-y-3 pb-4">
                      {/* 1. Media */}
                      {activeToggles.has('media') && (
                        <CreatorSectionCard id="variation-section-media-mobile" icon="photo_camera" title={t('media', { defaultValue: 'Media' })}>
                          <div className="space-y-3">
                            {/* Row 1: Pill toggle (right-aligned) */}
                            <div className="flex justify-end">
                              <div className="bg-gray-100 rounded-md p-0.5 flex">
                                <button
                                  type="button"
                                  onClick={() => { composer.setMediaMode('inherit'); composer.setMedia([]); }}
                                  disabled={isContentLocked}
                                  className={cn('flex items-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed', composer.mediaMode === 'inherit' ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700')}
                                >
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>link</span>
                                  {t('inherit_media_short', { defaultValue: 'Inherit' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => composer.setMediaMode('custom')}
                                  disabled={isContentLocked}
                                  className={cn('flex items-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed', composer.mediaMode === 'custom' ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700')}
                                >
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>photo_library</span>
                                  {t('custom_media_short', { defaultValue: 'Custom' })}
                                </button>
                              </div>
                            </div>

                            {/* Row 2: Parent post context */}
                            <div>
                              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{t('parent_post', { defaultValue: 'Parent Post' })}</p>
                              <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">{selectedPost.caption || t('no_caption', { defaultValue: 'No caption' })}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-gray-400">
                                  {selectedPost.postMedia && selectedPost.postMedia.length > 0
                                    ? `${selectedPost.postMedia.length} ${selectedPost.postMedia.length === 1 ? 'file' : 'files'}`
                                    : t('text_only', { defaultValue: 'Text only' })}
                                </span>
                                {!isEditMode && (
                                  <button type="button" onClick={() => setStep(1)} disabled={isBusy} className="text-[10px] font-medium text-gray-400 hover:text-gray-600 underline underline-offset-2 disabled:opacity-50">
                                    {t('change', { defaultValue: 'Change' })}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Row 2: Media content */}
                            {composer.mediaMode === 'inherit' ? (
                              /* Inherit: big grid of original post images */
                              selectedPost.postMedia && selectedPost.postMedia.length > 0 ? (
                                <div className={cn(
                                  'grid gap-2.5',
                                  selectedPost.postMedia.length === 1 ? 'grid-cols-1 max-w-[180px]' : 'grid-cols-3'
                                )}>
                                  {selectedPost.postMedia.map((m) => (
                                    <div
                                      key={m.id}
                                      className="relative group/thumb aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                                      onClick={() => setLightboxMedia(m)}
                                    >
                                      {m.type === 'VIDEO' && m.thumbnailUrl ? (
                                        <img src={m.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                                      ) : m.type === 'VIDEO' ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                          <Icon name={ICONS.VIDEO} size="lg" color="text-gray-500" ariaHidden />
                                        </div>
                                      ) : (
                                        <img src={m.thumbnailUrl || m.originalUrl || ''} alt="" className="w-full h-full object-cover" />
                                      )}
                                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                        <span className="material-symbols-outlined text-white" style={{ fontSize: '20px' }}>open_in_full</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center py-8 text-sm text-gray-400">
                                  {t('no_media_on_original', { defaultValue: 'Original post has no media' })}
                                </div>
                              )
                            ) : (
                              /* Custom: small reference thumbs + dropzone */
                              <div className="space-y-3">
                                {selectedPost.postMedia && selectedPost.postMedia.length > 0 && (
                                  <div className="flex gap-1.5 flex-wrap">
                                    {selectedPost.postMedia.slice(0, 6).map((m) => (
                                      <div key={m.id} className="w-10 h-10 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                                        <img src={m.thumbnailUrl || m.originalUrl || ''} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                    {selectedPost.postMedia.length > 6 && (
                                      <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-[10px] text-gray-400 font-medium flex-shrink-0">+{selectedPost.postMedia.length - 6}</div>
                                    )}
                                  </div>
                                )}
                                <MediaSection
                                  media={composer.media}
                                  onAddMedia={composer.addMedia}
                                  onRemoveMedia={composer.removeMedia}
                                  onReorderMedia={composer.reorderMedia}
                                  onSetMedia={composer.setMedia}
                                  onSetChangedThumbnail={composer.setChangedThumbnail}
                                  disabled={isBusy || isContentLocked}
                                  showCarouselMode={activeToggles.has('carousel') || composer.isCarouselMode}
                                  selectedPlatforms={selectedPlatforms()}
                                />
                              </div>
                            )}
                          </div>
                        </CreatorSectionCard>
                      )}
                      {/* 2. AI */}
                      {activeToggles.has('ai') && (
                        <CreatorSectionCard id="variation-section-ai-mobile" icon="auto_awesome" title={t('ai_assistant', { defaultValue: 'AI Assistant' })}>
                          <AISection caption={composer.caption} onCaptionChange={composer.setCaption} selectedChannels={composer.selectedChannels} disabled={isBusy} />
                        </CreatorSectionCard>
                      )}
                      {/* 3. Hashtags */}
                      {activeToggles.has('hashtags') && (
                        <CreatorSectionCard id="variation-section-hashtags-mobile" icon="tag" title={t('hashtags', { defaultValue: 'Hashtags' })}>
                          <HashtagSection caption={composer.caption} onCaptionChange={composer.setCaption} readOnly={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                      {/* 4. Labels */}
                      {activeToggles.has('labels') && (
                        <CreatorSectionCard id="variation-section-labels-mobile" icon="label" title={t('organize', { defaultValue: 'Organize' })}>
                          <LabelsSection labels={composer.labels} onChange={composer.setLabels} disabled={isBusy || isContentLocked} />
                        </CreatorSectionCard>
                      )}
                      {/* 5. Channels */}
                      {scheduleVisible && (
                        <CreatorSectionCard id="variation-section-channels-mobile" icon="rocket_launch" title={t('platforms', { defaultValue: 'Channels' })}>
                          <PlatformsSection
                            selectedChannels={composer.selectedChannels}
                            onSelectionChange={composer.setChannels}
                            disabled={isBusy}
                            mode="shoot_now"
                            mediaTypes={currentMediaTypes()}
                            channelOptions={composer.channelOptions}
                            onOptionsChange={composer.updateChannelOptions}
                            hasVideo={hasVideo}
                            hasMedia={composer.media.length > 0}
                            caption={composer.caption}
                            media={previewMedia}
                            isCarouselMode={composer.isCarouselMode}
                            channels={channelPreviewData}
                          />
                        </CreatorSectionCard>
                      )}
                      {/* 6. Schedule */}
                      {scheduleVisible && (
                        <CreatorSectionCard id="variation-section-schedule-mobile" icon="schedule" title={t('schedule', { defaultValue: 'Schedule' })}>
                          <div className="space-y-4">
                            {showNowLaterPill && (
                              <div className="bg-gray-100 rounded-md p-0.5 flex">
                                <button type="button" onClick={() => setScheduleMode('shoot_now')} className={cn('flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all', scheduleMode === 'shoot_now' ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700')}>
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>rocket_launch</span>
                                  {t('now', { defaultValue: 'Now' })}
                                </button>
                                <button type="button" onClick={() => setScheduleMode('schedule')} className={cn('flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all', scheduleMode === 'schedule' ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700')}>
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>schedule</span>
                                  {t('later', { defaultValue: 'Later' })}
                                </button>
                              </div>
                            )}
                            {!isShootingMode && (
                              <>
                                <CustomSelect label={t('select_calendar', { defaultValue: 'Calendar' })} placeholder={t('select_calendar_placeholder', { defaultValue: 'Select calendar' })} options={calendarOptions} value={selectedCalendar} onChange={handleCalendarChange} disabled={isBusy || isLoadingCalendars} compact />
                                {selectedCalendar && eventOptions.length > 0 && (
                                  <CustomSelect label={t('add_to_campaign', { defaultValue: 'Campaign' })} placeholder={t('none', { defaultValue: 'None' })} options={eventOptions} value={selectedEvent} onChange={setSelectedEvent} disabled={isBusy || isLoadingEvents} helperText={t('campaign_helper', { defaultValue: 'Optionally link to a campaign or holiday' })} compact />
                                )}
                              </>
                            )}
                            {scheduleMode === 'schedule' && (
                              <ScheduleSection scheduledTime={composer.scheduledTime} onScheduleChange={composer.setScheduledTime} disabled={isBusy} compact />
                            )}
                          </div>
                        </CreatorSectionCard>
                      )}
                    </div>
                  )}
                </div>

                {/* ── RESIZE HANDLE (lg+ only) ── */}
                {hasRightPanelToggles && (
                  <div
                    className="hidden lg:flex items-stretch cursor-col-resize group select-none flex-shrink-0"
                    {...resizeHandleProps}
                  >
                    <div className="w-1 mx-0.5 rounded-full bg-gray-200 group-hover:bg-primary-400 group-active:bg-primary-500 transition-colors my-4" />
                  </div>
                )}

                {/* ── RIGHT COLUMN: Toggled sections (lg+ only, only when right-panel toggles active) ── */}
                {hasRightPanelToggles && (
                  <div
                    style={hasRightPanelToggles ? { width: `${100 - resizeLeftPercent}%` } : undefined}
                    className="hidden lg:flex lg:flex-col min-h-0 min-w-0 flex-1 max-lg:!w-auto"
                  >
                    {/* Top scroll fade — outside scroll container, overlaps */}
                    <div className={cn(
                      'flex-shrink-0 h-6 -mb-6 relative z-10 pointer-events-none bg-gradient-to-b from-white to-transparent transition-opacity duration-200',
                      canScrollUp ? 'opacity-100' : 'opacity-0'
                    )} />
                    <div
                      ref={scrollRef}
                      className="flex-1 overflow-y-auto pl-4 pr-6 sm:pr-[33px] pt-4 pb-5"
                    >
                      {/* Media Section — vertical stack */}
                      {activeToggles.has('media') && (
                        <CreatorSectionCard id="variation-section-media" icon="photo_camera" title={t('media', { defaultValue: 'Media' })}>
                          <div className="space-y-3">
                            {/* Row 1: Parent post context (left) + Pill toggle (right) */}
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{t('parent_post', { defaultValue: 'Parent Post' })}</p>
                                <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">{selectedPost.caption || t('no_caption', { defaultValue: 'No caption' })}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] text-gray-400">
                                    {selectedPost.postMedia && selectedPost.postMedia.length > 0
                                      ? `${selectedPost.postMedia.length} ${selectedPost.postMedia.length === 1 ? 'file' : 'files'}`
                                      : t('text_only', { defaultValue: 'Text only' })
                                    }
                                  </span>
                                  {!isEditMode && (
                                    <button
                                      type="button"
                                      onClick={() => setStep(1)}
                                      disabled={isBusy}
                                      className="text-[10px] font-medium text-gray-400 hover:text-gray-600 underline underline-offset-2 disabled:opacity-50"
                                    >
                                      {t('change', { defaultValue: 'Change' })}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="bg-gray-100 rounded-md p-0.5 flex flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    composer.setMediaMode('inherit');
                                    composer.setMedia([]);
                                  }}
                                  disabled={isContentLocked}
                                  className={cn(
                                    'flex items-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                                    composer.mediaMode === 'inherit'
                                      ? 'bg-gray-900 text-white shadow-sm'
                                      : 'bg-transparent text-gray-500 hover:text-gray-700'
                                  )}
                                >
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>link</span>
                                  {t('inherit_media_short', { defaultValue: 'Inherit' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => composer.setMediaMode('custom')}
                                  disabled={isContentLocked}
                                  className={cn(
                                    'flex items-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                                    composer.mediaMode === 'custom'
                                      ? 'bg-gray-900 text-white shadow-sm'
                                      : 'bg-transparent text-gray-500 hover:text-gray-700'
                                  )}
                                >
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>photo_library</span>
                                  {t('custom_media_short', { defaultValue: 'Custom' })}
                                </button>
                              </div>
                            </div>

                            {/* Row 2: Media content */}
                            {composer.mediaMode === 'inherit' ? (
                              /* Inherit: big grid of original post images */
                              selectedPost.postMedia && selectedPost.postMedia.length > 0 ? (
                                <div className={cn(
                                  'grid gap-2.5',
                                  selectedPost.postMedia.length === 1 ? 'grid-cols-1 max-w-[180px]' : 'grid-cols-3'
                                )}>
                                  {selectedPost.postMedia.map((m) => (
                                    <div
                                      key={m.id}
                                      className="relative group/thumb aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                                      onClick={() => setLightboxMedia(m)}
                                    >
                                      {m.type === 'VIDEO' && m.thumbnailUrl ? (
                                        <img src={m.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                                      ) : m.type === 'VIDEO' ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                          <Icon name={ICONS.VIDEO} size="lg" color="text-gray-500" ariaHidden />
                                        </div>
                                      ) : (
                                        <img src={m.thumbnailUrl || m.originalUrl || ''} alt="" className="w-full h-full object-cover" />
                                      )}
                                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                        <span className="material-symbols-outlined text-white" style={{ fontSize: '20px' }}>open_in_full</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center py-8 text-sm text-gray-400">
                                  {t('no_media_on_original', { defaultValue: 'Original post has no media' })}
                                </div>
                              )
                            ) : (
                              /* Custom: small reference thumbs + dropzone */
                              <div className="space-y-3">
                                {selectedPost.postMedia && selectedPost.postMedia.length > 0 && (
                                  <div className="flex gap-1.5 flex-wrap">
                                    {selectedPost.postMedia.slice(0, 6).map((m) => (
                                      <div key={m.id} className="w-10 h-10 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                                        <img src={m.thumbnailUrl || m.originalUrl || ''} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                    {selectedPost.postMedia.length > 6 && (
                                      <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-[10px] text-gray-400 font-medium flex-shrink-0">
                                        +{selectedPost.postMedia.length - 6}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <MediaSection
                                  media={composer.media}
                                  onAddMedia={composer.addMedia}
                                  onRemoveMedia={composer.removeMedia}
                                  onReorderMedia={composer.reorderMedia}
                                  onSetMedia={composer.setMedia}
                                  onSetChangedThumbnail={composer.setChangedThumbnail}
                                  disabled={isBusy || isContentLocked}
                                  showCarouselMode={activeToggles.has('carousel') || composer.isCarouselMode}
                                  selectedPlatforms={selectedPlatforms()}
                                />
                              </div>
                            )}
                          </div>
                        </CreatorSectionCard>
                      )}

                      {/* AI Section */}
                      {activeToggles.has('ai') && (
                        <CreatorSectionCard id="variation-section-ai" icon="auto_awesome" title={t('ai_assistant', { defaultValue: 'AI Assistant' })}>
                          <AISection
                            caption={composer.caption}
                            onCaptionChange={composer.setCaption}
                            selectedChannels={composer.selectedChannels}
                            disabled={isBusy}
                          />
                        </CreatorSectionCard>
                      )}

                      {/* Channels Section */}
                      {scheduleVisible && (
                        <CreatorSectionCard id="variation-section-channels" icon="rocket_launch" title={t('platforms', { defaultValue: 'Channels' })}>
                          <PlatformsSection
                            selectedChannels={composer.selectedChannels}
                            onSelectionChange={composer.setChannels}
                            disabled={isBusy}
                            mode="shoot_now"
                            mediaTypes={currentMediaTypes()}
                            channelOptions={composer.channelOptions}
                            onOptionsChange={composer.updateChannelOptions}
                            hasVideo={hasVideo}
                            hasMedia={composer.media.length > 0}
                            caption={composer.caption}
                            media={previewMedia}
                            isCarouselMode={composer.isCarouselMode}
                            channels={channelPreviewData}
                          />
                        </CreatorSectionCard>
                      )}

                      {/* Schedule Section */}
                      {scheduleVisible && (
                        <CreatorSectionCard id="variation-section-schedule" icon="schedule" title={t('schedule', { defaultValue: 'Schedule' })}>
                          <div className="space-y-4">
                            {showNowLaterPill && (
                              <div className="bg-gray-100 rounded-md p-0.5 flex">
                                <button
                                  type="button"
                                  onClick={() => setScheduleMode('shoot_now')}
                                  className={cn(
                                    'flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all',
                                    scheduleMode === 'shoot_now'
                                      ? 'bg-gray-900 text-white shadow-sm'
                                      : 'bg-transparent text-gray-500 hover:text-gray-700'
                                  )}
                                >
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>rocket_launch</span>
                                  {t('now', { defaultValue: 'Now' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setScheduleMode('schedule')}
                                  className={cn(
                                    'flex-1 flex items-center justify-center gap-1 py-1 px-2 text-xs font-medium rounded transition-all',
                                    scheduleMode === 'schedule'
                                      ? 'bg-gray-900 text-white shadow-sm'
                                      : 'bg-transparent text-gray-500 hover:text-gray-700'
                                  )}
                                >
                                  {/* eslint-disable-next-line i18next/no-literal-string */}
                                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>schedule</span>
                                  {t('later', { defaultValue: 'Later' })}
                                </button>
                              </div>
                            )}
                            {!isShootingMode && (
                              <>
                                <CustomSelect
                                  label={t('select_calendar', { defaultValue: 'Calendar' })}
                                  placeholder={t('select_calendar_placeholder', { defaultValue: 'Select calendar' })}
                                  options={calendarOptions}
                                  value={selectedCalendar}
                                  onChange={handleCalendarChange}
                                  disabled={isBusy || isLoadingCalendars}
                                  compact
                                />
                                {selectedCalendar && eventOptions.length > 0 && (
                                  <CustomSelect
                                    label={t('add_to_campaign', { defaultValue: 'Campaign' })}
                                    placeholder={t('none', { defaultValue: 'None' })}
                                    options={eventOptions}
                                    value={selectedEvent}
                                    onChange={setSelectedEvent}
                                    disabled={isBusy || isLoadingEvents}
                                    helperText={t('campaign_helper', { defaultValue: 'Optionally link to a campaign or holiday' })}
                                    compact
                                  />
                                )}
                              </>
                            )}
                            {scheduleMode === 'schedule' && (
                              <ScheduleSection
                                scheduledTime={composer.scheduledTime}
                                onScheduleChange={composer.setScheduledTime}
                                disabled={isBusy}
                                compact
                              />
                            )}
                          </div>
                        </CreatorSectionCard>
                      )}

                    </div>
                    {/* Bottom scroll fade — outside scroll container, overlaps */}
                    <div className={cn(
                      'flex-shrink-0 h-6 -mt-6 relative z-10 pointer-events-none bg-gradient-to-t from-white to-transparent transition-opacity duration-200',
                      canScrollDown ? 'opacity-100' : 'opacity-0'
                    )} />
                  </div>
                )}
              </div>

              {/* Footer - CTA matching PostCreator */}
              <ModalFooter noBorder justify="center-lg-end" className="flex-shrink-0 !px-6 sm:!px-[33px] !pt-4 !pb-5">
                <CancelButton onClick={handleClose} disabled={isBusy} tooltip={t('cancel', { defaultValue: 'Cancel' })} />

                <Tooltip content={disabledReason || ''} side="top" disabled={!disabledReason}>
                <div className="relative">
                  {ctaConfig.showDropdown ? (
                    <div className="flex">
                      <button
                        type="button"
                        onClick={handlePrimaryAction}
                        disabled={isDisabled}
                        className={cn(
                          'w-[9rem] sm:w-[11rem] h-[44px] sm:h-10 flex items-center justify-center gap-1.5 sm:gap-2 rounded-l-md',
                          'bg-primary-400 text-primary-400-contrast font-semibold text-xs sm:text-sm',
                          'border border-primary-400',
                          'hover:bg-primary-600 hover:border-primary-600',
                          'transition-colors duration-200',
                          'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                        )}
                      >
                        {isBusy ? (
                          <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <>
                            <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>{ctaConfig.materialIcon}</span>
                            {ctaConfig.label}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCtaDropdown(!showCtaDropdown)}
                        disabled={menuOnly ? isBusy || releasePerms.isLoading : isDisabled}
                        className={cn(
                          'w-[44px] sm:w-10 h-[44px] sm:h-10 flex items-center justify-center rounded-r-md border-l',
                          'bg-primary-400 text-primary-400-contrast border-primary-600',
                          'hover:bg-primary-600 transition-colors duration-200',
                          'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                        )}
                      >
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        <span className="material-symbols-outlined text-[16px]">expand_more</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handlePrimaryAction}
                      disabled={isDisabled}
                      className={cn(
                        'w-[10rem] sm:w-[13rem] h-[44px] sm:h-10 flex items-center justify-center gap-1.5 sm:gap-2 rounded-md',
                        'bg-primary-400 text-primary-400-contrast font-semibold text-xs sm:text-sm',
                        'border border-primary-400',
                        'hover:bg-primary-600 hover:border-primary-600',
                        'transition-colors duration-200',
                        'disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                      )}
                    >
                      {isBusy ? (
                        <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <>
                          <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>{ctaConfig.materialIcon}</span>
                          {ctaConfig.label}
                        </>
                      )}
                    </button>
                  )}

                  {/* Dropdown for "Save & Mark Ready" */}
                  {showCtaDropdown && ctaConfig.showDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowCtaDropdown(false)} />
                      <div className="absolute right-0 bottom-full mb-2 w-60 sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                        {!permissions.workflowEnabled && (
                          <button
                            onClick={() => {
                              setShowCtaDropdown(false);
                              composer.handleSaveAndMarkReady();
                            }}
                            disabled={isDisabled}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            <span className="material-symbols-outlined" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>check_circle</span>
                            {t('save_and_mark_ready', { defaultValue: 'Save & Mark Ready' })}
                          </button>
                        )}
                        {showReleaseDirect && (
                          <button
                            type="button"
                            data-testid="composer-save-and-release"
                            onClick={() => {
                              setShowCtaDropdown(false);
                              void runSaveAction(composer.handleSaveAndRelease);
                            }}
                            disabled={isBusy || releasePerms.isLoading || !composer.canSaveDraft}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name={ICONS.TICK_CIRCLE} size="sm" ariaHidden />
                            {effectiveVariation ? t('composer_save_and_release') : t('composer_create_and_release')}
                          </button>
                        )}
                        {showSaveAndSendForReview && (
                          <button
                            type="button"
                            data-testid="composer-send-for-review"
                            onClick={() => {
                              setShowCtaDropdown(false);
                              void runSaveAction(composer.handleSaveAndSendForReview);
                            }}
                            disabled={isBusy || releasePerms.isLoading || !composer.canSaveDraft}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name={ICONS.PAPER_PLANE} size="sm" ariaHidden />
                            {t('composer_save_and_send_for_review')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                </Tooltip>
              </ModalFooter>
            </>
          )}
        </Modal>
      </TooltipProvider>

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title={t('discard_changes', { defaultValue: 'Discard Changes?' })}
        description={t('unsaved_changes_lost', { defaultValue: 'Your unsaved changes will be lost.' })}
        variant="danger"
        confirmText={t('discard', { defaultValue: 'Discard' })}
        cancelText={t('continue', { defaultValue: 'Continue Editing' })}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
        closeOnConfirm={false}
      />

      {/* Media Preview Lightbox — Radix Dialog to properly layer above parent modal */}
      <Dialog.Root open={!!lightboxMedia} onOpenChange={(open) => !open && setLightboxMedia(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center">
            <Dialog.Content className="relative z-[10000] focus:outline-none max-w-[90vw] max-h-[90vh] overflow-auto">
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Dialog.Title className="sr-only">Media Preview</Dialog.Title>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Dialog.Description className="sr-only">Full size media preview</Dialog.Description>
              <Dialog.Close
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white hover:text-gray-200 flex items-center justify-center transition-all duration-200 z-10 focus:outline-none"
                aria-label={t('common:close', { defaultValue: 'Close' })}
              >
                <Icon name={ICONS.CLOSE} size="sm" />
              </Dialog.Close>
              {lightboxMedia?.type === 'IMAGE' ? (
                // Use originalUrl when materialized. For deferred GDrive,
                // request a high-res thumbnail directly from Drive (the small
                // default thumbnailUrl is ~200px and looks awful at modal size).
                <img
                  src={
                    lightboxMedia.originalUrl ||
                    (lightboxMedia.sourceRef?.driveFileId
                      ? `https://drive.google.com/thumbnail?id=${lightboxMedia.sourceRef.driveFileId}&sz=w2000`
                      : lightboxMedia.thumbnailUrl) ||
                    ''
                  }
                  alt=""
                  className="max-w-full max-h-[85vh] object-contain rounded-lg"
                />
              ) : lightboxMedia?.type === 'VIDEO' ? (
                (lightboxMedia.sourceType === 'GDRIVE' && (!lightboxMedia.originalUrl || lightboxMedia.originalUrl === '') && lightboxMedia.sourceRef?.driveFileId) ? (
                  <iframe
                    src={`https://drive.google.com/file/d/${lightboxMedia.sourceRef.driveFileId}/preview`}
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                    className="max-w-full max-h-[85vh] rounded-lg"
                    title="Google Drive preview"
                    style={{ width: '80vw', maxWidth: '900px', height: '85vh' }}
                  />
                ) : (
                  <video
                    src={lightboxMedia.originalUrl ?? ''}
                    controls
                    autoPlay
                    className="max-w-full max-h-[85vh] rounded-lg"
                  />
                )
              ) : lightboxMedia?.type === 'PDF' ? (
                <iframe
                  src={lightboxMedia.originalUrl ?? ''}
                  className="w-[80vw] max-w-4xl rounded-lg bg-white"
                  style={{ height: '85vh' }}
                  title="PDF Document"
                />
              ) : null}
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
